import React from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuUndo2, LuRedo2 } from "react-icons/lu";
import CanvasFontButton from "./CanvasFontButton";
import "./_canvasOverlay.scss";

// Grid / Theme glyphs mirror the icons used elsewhere in the editor but draw
// with currentColor so the button can flip color on hover / active state.
const GridIcon = () => (
  <svg width="19" height="19" viewBox="0 0 21 20" fill="none">
    <rect x="2" y="2" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4" />
    <rect x="13" y="2" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4" />
    <rect x="2" y="12" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4" />
    <rect x="13" y="12" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const ThemeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" stroke="none" />
    <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" stroke="none" />
    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" stroke="none" />
    <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" stroke="none" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
  </svg>
);

/**
 * Floating toolbar at the top-left of the canvas holding the scene-level
 * actions (undo / redo / grid / theme / font). Element-level actions stay in
 * the Scene side panel.
 */
const CanvasTopToolbar = ({
  onUndo,
  onRedo,
  undoDisabled,
  redoDisabled,
  gridActive,
  onToggleGrid,
  onOpenTheme,
  fontFamily,
  onSelectFont,
  onPreviewFont,
  style,
}) => {
  const run = (fn) => (e) => {
    fn();
    e.currentTarget.blur();
  };

  return (
    <div className="canvasOverlay canvasOverlay--topLeft" style={style}>
      <button
        type="button"
        className="canvasOverlayBtn"
        title={i18n.t("Undo")}
        disabled={undoDisabled}
        onClick={run(onUndo)}
      >
        <LuUndo2 size={19} />
      </button>
      <button
        type="button"
        className="canvasOverlayBtn"
        title={i18n.t("Redo")}
        disabled={redoDisabled}
        onClick={run(onRedo)}
      >
        <LuRedo2 size={19} />
      </button>

      <span className="canvasOverlayDivider" />

      <button
        type="button"
        className={"canvasOverlayBtn" + (gridActive ? " is-active" : "")}
        title={i18n.t("Grid")}
        onClick={run(onToggleGrid)}
      >
        <GridIcon />
      </button>
      <button
        type="button"
        className="canvasOverlayBtn"
        title={i18n.t("ThemePalette.Theme")}
        onClick={run(onOpenTheme)}
      >
        <ThemeIcon />
      </button>
      <CanvasFontButton value={fontFamily} onSelect={onSelectFont} onPreview={onPreviewFont} />
    </div>
  );
};

CanvasTopToolbar.propTypes = {
  onUndo: PropTypes.func.isRequired,
  onRedo: PropTypes.func.isRequired,
  undoDisabled: PropTypes.bool,
  redoDisabled: PropTypes.bool,
  gridActive: PropTypes.bool,
  onToggleGrid: PropTypes.func.isRequired,
  onOpenTheme: PropTypes.func.isRequired,
  /** Current scene font — drives the font picker's active row. */
  fontFamily: PropTypes.string,
  /** (fontFamily, allScenes) — applies the font to text boxes. */
  onSelectFont: PropTypes.func.isRequired,
  /** (fontFamily|null) — live preview on the current scene's text boxes. */
  onPreviewFont: PropTypes.func,
  style: PropTypes.object,
};

export default CanvasTopToolbar;
